import React, { FC, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Button, CircularProgress } from '@mui/material';

import { Application } from 'reduxState/store/application/types';
import CampaignManagerImageUpload from 'components/Campaigns/CampaignForms/CampaignManagerImageUpload/CampaignManagerImageUploader';
import { displayTypeOptions, frequencyTypeOptions } from 'components/Campaigns/CampaignForms/constants';
import DropdownSearchSelect, { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import { ICampaign } from 'reduxState/store/campaigns/types';
import TextInput from '../../../TextInput/TextInput';
import MerchantSearchField from '../MerchantSearchField/MerchantSearchField';
import CampaignBulkAddField from '../CampaignBulkAddField/CampaignBulkAddField';
import CampaignDisplayTypeFields from '../CampaignDisplayTypeFields/CampaignDisplayTypeFields';
import { useCampaignForm } from '../hooks/useCampaignForm';
import CampaignURLPatternsField from '../CampaignURLPatternsField/CampaignURLPatternsField';

/**
 * CreateCampaignForm component for creating new marketing campaigns.
 *
 * This component provides a form interface for creating and configuring marketing campaigns
 * that can be applied across multiple applications. It handles form validation, image uploads,
 * and campaign duplication functionality.
 *
 * The form includes fields for:
 * - Basic campaign details (title, dates, merchant ID)
 * - Content configuration (headline, main text, CTA)
 * - HTML insertion settings
 * - Display settings and frequency caps
 * - URL pattern and search phrase targeting
 * - Image management
 *
 * The component supports:
 * - Multi-application targeting
 * - Real-time field validation
 * - Image upload and management
 * - Duplication of existing campaigns
 * - Form state management and error handling
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Application[]} props.selectedApps - Array of applications selected for campaign targeting
 * @param {(hasError: boolean) => void} props.setHasSelectedAppsError - Callback to set application selection error state
 * @param {() => void} props.resetSelectedApps - Callback to clear selected applications
 * @param {ICampaign | null} props.campaignToDuplicate - Campaign to use as template for duplication (optional)
 * @param {(campaign: ICampaign | null) => void} props.setCampaignToDuplicate - Callback to update campaign duplication source
 * @returns {JSX.Element} A form interface for creating and configuring marketing campaigns
 */

/** Partner display name for title: same partner (e.g. "Acorns" from "Acorns Chrome extension" + "Acorns iOS app") or "Multi-Partner". */
function getPartnerDisplayName(selectedApps: Application[]): string {
  if (selectedApps.length === 0) return '';
  const getBaseName = (name: string) =>
    name.replace(/\s+(Chrome\s+)?[Ee]xtension$/i, '').replace(/\s+iOS\s+[Aa]pp$/i, '').trim() || name;
  const firstWord = (s: string) => s.split(/\s+/)[0] || s;
  const names = selectedApps.map(app => firstWord(getBaseName(app.name)));
  const first = names[0];
  const allSamePartner = names.length > 0 && names.every(n => n === first);
  return allSamePartner ? first : 'Multi-Partner';
}

interface ICreateCampaignForm {
  selectedApps: Application[];
  campaignToDuplicate: ICampaign | null;
  setHasSelectedAppsError: (hasError: boolean) => void;
  resetSelectedApps: () => void;
  onDraftRestored?: (selectedApps: Application[]) => void;
  /** When true, draft restore runs when modal opens (so reopening after e.g. re-login restores the draft). */
  isCreateModalOpen?: boolean;
  setCampaignToDuplicate: (campaign: ICampaign | null) => void;
  handleCloseModal: () => void;
  /** Test-only: when true, Campaign Title field is shown without requiring merchant/start date (for stable tests). */
  forceShowTitleField?: boolean;
  /** Called when form dirty state changes (so parent can skip discard confirm when no changes). */
  onHasFormChangesChange?: (hasChanges: boolean) => void;
}

const CreateCampaignForm: FC<ICreateCampaignForm> = ({
  selectedApps,
  campaignToDuplicate,
  setHasSelectedAppsError,
  resetSelectedApps,
  onDraftRestored,
  isCreateModalOpen,
  setCampaignToDuplicate,
  handleCloseModal,
  forceShowTitleField = false,
  onHasFormChangesChange,
}) => {
  // Create a stable reference for minimum date to prevent validation issues as time passes
  const minDateTime = useMemo(() => new Date(), []);

  const {
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    setHTMLPopUpTemplateFormData,
    htmlPopUpTemplateFormData,
    uploadedImages,
    isLoading,
    networkMerchant,
    urlPatternsError,
    insertionOrderError,
    hasAttemptedSubmit,
    shouldUpdateStartDateToNow,
    setNetworkMerchant,
    handleInputChange,
    handleNumberInputChange,
    handleAddUrlPattern,
    handleRemoveUrlPattern,
    handleUpdateUrlPattern,
    handleAddSearchPhrase,
    handleRemoveSearchPhrase,
    handleSetUploadedImages,
    handleDisplayTypeChange,
    handleStartDateManualChange,
    handleStartDateNowClick,
    handleSubmit,
    handleClearForm,
    setUrlPatternsError,
    setShouldUpdateStartDateToNow,
    skipNextTitleAutoGenerationRef,
    hasFormChanges,
  } = useCampaignForm({
    mode: 'create',
    selectedApps,
    setHasSelectedAppsError,
    resetSelectedApps,
    handleCloseModal,
    onDraftRestored,
    isCreateModalOpen,
    campaignToDuplicate,
    setCampaignToDuplicate,
  });

  const hasPartner = selectedApps.length > 0;
  const hasMerchant = !!formData.merchantId?.trim();
  const hasStartDate = !!formData.startDate || shouldUpdateStartDateToNow;
  // Show title when: test override, or when we have partner + merchant + start date (strict: all three required)
  const showTitleField = forceShowTitleField || (hasPartner && hasMerchant && hasStartDate);

  useEffect(() => {
    onHasFormChangesChange?.(hasFormChanges);
  }, [hasFormChanges, onHasFormChangesChange]);

  // Track last auto-generated title and whether user has manually edited (so we don't overwrite)
  const lastGeneratedTitleRef = React.useRef<string>('');
  const userHasEditedTitleRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!showTitleField) return;
    if (skipNextTitleAutoGenerationRef.current) {
      skipNextTitleAutoGenerationRef.current = false;
      lastGeneratedTitleRef.current = '';
      userHasEditedTitleRef.current = false;
      return;
    }
    if (userHasEditedTitleRef.current) return;
    const partnerName = getPartnerDisplayName(selectedApps);
    const merchantName =
      formData.merchantDisplayName?.trim() || (formData.merchantId ? 'Merchant' : '');
    const startDateStr = shouldUpdateStartDateToNow
      ? 'At submission'
      : formData.startDate
        ? format(formData.startDate, 'yyyy-MM-dd')
        : '';
    const generated = `${partnerName} - ${merchantName} - ${startDateStr}`;
    // If current title is not empty and not our last generated value, user has customized
    if (formData.title !== '' && formData.title !== lastGeneratedTitleRef.current) {
      userHasEditedTitleRef.current = true;
      return;
    }
    lastGeneratedTitleRef.current = generated;
    setFormData(prev => ({ ...prev, title: generated }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showTitleField,
    formData.title,
    selectedApps,
    formData.merchantId,
    formData.merchantDisplayName,
    formData.startDate,
    shouldUpdateStartDateToNow,
    setFormData,
  ]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-montserrat min-w-0 max-w-full">
      <div className="min-w-0">
        <h3 className="text-muted-dark-purple">Campaign Details</h3>
        <div className="flex flex-col flex-1">
          <DropdownSearchSelect
            value={formData.displayType}
            label="Display Type"
            options={displayTypeOptions}
            placeholder="Select a Display Type"
            handleChange={handleDisplayTypeChange}
            hasError={formErrors.displayType}
            errorMessage={'Display type is required'}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 my-[8px]">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col xs:flex-row gap-2 items-stretch xs:items-center">
                <Button
                  className="bg-light-grey text-dark-grey hover:bg-dark-purple hover:text-white disabled:opacity-50 disabled:cursor-not-allowed normal-case rounded-2xl shrink-0"
                  onClick={handleStartDateNowClick}
                >
                  Now
                </Button>
                <DateTimePicker
                  label={shouldUpdateStartDateToNow ? 'Will get start date/time at submission' : 'Start Date'}
                  value={shouldUpdateStartDateToNow ? null : formData.startDate}
                  onChange={handleStartDateManualChange}
                  minDateTime={minDateTime}
                  closeOnSelect={false}
                  sx={{
                    width: '100%',
                    maxWidth: 280,
                  }}
                  slotProps={{
                    textField: {
                      error: !!formErrors.startDate,
                      helperText: formErrors.startDate ? 'Start date is required' : undefined,
                    },
                  }}
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <DateTimePicker
                label="End Date"
                value={formData.endDate}
                closeOnSelect={false}
                minDateTime={formData.startDate || minDateTime}
                onChange={(date: Date | null) => {
                  if (date) {
                    setFormErrors(prev => ({ ...prev, endDate: false }));
                    setFormData(prev => ({ ...prev, endDate: date }));
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: 280,
                }}
                slotProps={{
                  textField: {
                    error: !!formErrors.endDate,
                    helperText: formErrors.endDate ? 'End date is required' : undefined,
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        </div>
        <div className="flex flex-col flex-1">
          <MerchantSearchField
            value={formData.merchantId}
            onChange={(value: string) => handleInputChange(value, 'merchantId')}
            onMerchantSelect={(_, name) =>
              setFormData(prev => ({ ...prev, merchantDisplayName: name }))
            }
            label="Merchant ID"
            placeholder="Search by merchant name or enter Merchant ID"
            hasError={formErrors.merchantId}
            errorText="Merchant ID is required"
          />
        </div>
        {showTitleField && (
          <div className="flex flex-col flex-1" data-testid="campaign-title-field">
            <TextInput
              label="Campaign Title"
              value={formData.title}
              placeholderText="Enter Campaign Title"
              helperText="Auto-generated from partner, merchant, and start date; you can edit it."
              handleChange={(value: string) => {
                if (value.trim() !== '') userHasEditedTitleRef.current = true;
                handleInputChange(value, 'title');
              }}
              hasError={formErrors.title}
              errorText="Title is required"
              inputProps={{ 'data-testid': 'campaign-title-input' }}
            />
          </div>
        )}
        <div className="flex flex-col flex-1">
          <TextInput
            label="Destination URL"
            value={formData.destinationURL}
            placeholderText="Enter Destination URL"
            handleChange={(value: string) => {
              handleInputChange(value, 'destinationURL');
            }}
            hasError={formErrors.destinationURL}
            errorText={'Valid Destination URL is required'}
          />
        </div>
        <div className="flex flex-col flex-1 mb-2">
          <TextInput
            label="SERP Tagline"
            value={formData.serpTagline}
            hasError={formErrors.serpTagline}
            placeholderText="Enter SERP Tagline"
            handleChange={(value: string) => {
              handleInputChange(value, 'serpTagline');
            }}
          />
        </div>
        <div className="flex flex-col flex-1 mb-2">
          <TextInput
            label="Google Sheet ID"
            value={formData.googleSheetID}
            placeholderText="Enter Google Sheet ID"
            handleChange={(value: string) => {
              handleInputChange(value, 'googleSheetID');
            }}
          />
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col flex-1">
            <TextInput
              label="Insertion Order ID"
              value={formData.insertionOrderId}
              placeholderText="Enter Insertion Order ID"
              handleChange={(value: string) => {
                handleInputChange(value, 'insertionOrderId');
                setNetworkMerchant(null);
              }}
              hasError={formErrors.insertionOrderId}
              errorText={insertionOrderError || 'Insertion Order ID is required'}
              helperText={
                networkMerchant ? `Network Merchant: ${networkMerchant?.id} - ${networkMerchant?.name}` : undefined
              }
            />
          </div>
          <div className="flex flex-col flex-1">
            <TextInput
              label="Insertion Order Value"
              value={formData.insertionOrderValue}
              placeholderText="Enter Insertion Order Value"
              handleChange={(value: string) => {
                handleInputChange(value, 'insertionOrderValue');
              }}
              hasError={formErrors.insertionOrderValue}
              errorText={'Insertion Order Value is required'}
            />
          </div>
        </div>
        <CampaignURLPatternsField
          hasAttemptedSubmit={hasAttemptedSubmit}
          handleAddUrlPattern={handleAddUrlPattern}
          handleRemoveUrlPattern={handleRemoveUrlPattern}
          handleUpdateUrlPattern={handleUpdateUrlPattern}
          valuesArray={formData.urlPatterns2}
          hasError={formErrors.urlPatterns2 || formErrors.urlPatterns}
          setUrlPatternsError={setUrlPatternsError}
          urlPatternsError={urlPatternsError}
        />
        <CampaignBulkAddField
          valueArray={formData.searchPhrases}
          handleAddValue={handleAddSearchPhrase}
          handleRemoveValue={handleRemoveSearchPhrase}
          error={formErrors.searchPhrases}
          title="Search Phrases"
          placeholder="Enter search phrase"
        />
        <div>
          <h4 className="mb-2 text-muted-dark-purple">Frequency Cap</h4>
          <div className="flex gap-4">
            <div className="flex flex-col flex-1">
              <DropdownSearchSelect
                value={formData.frequencyType}
                label="Type"
                options={frequencyTypeOptions}
                placeholder="Select a type "
                handleChange={(option: IDropdownSelectOption | null) => {
                  if (option) {
                    setFormErrors(prev => ({ ...prev, frequencyType: false }));
                  }
                  setFormData(prev => ({ ...prev, frequencyType: option }));
                }}
                hasError={formErrors.frequencyType}
                errorMessage={'Frequency type is required'}
                tooltipText="DISMISSAL: Counts when user closes the campaign. VIEW_COUNT: Counts each time campaign is shown. Example: Use DISMISSAL for pop-ups that users can close, VIEW_COUNT for inline content."
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Minutes"
                value={formData.minutes !== null ? formData.minutes : ''}
                placeholderText="Enter a positive minute value"
                inputType="number"
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'minutes')}
                hasError={formErrors.minutes}
                errorText={'Valid minute value is required'}
                tooltipText="Time window for counting views/dismissals. Common values: 60 (1 hour), 1440 (24 hours), 10080 (1 week). Example: 1440 minutes = campaign can show up to [Limit] times per day."
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Limit"
                inputType="number"
                placeholderText="Enter a positive limit value"
                value={formData.limit !== null ? formData.limit : ''}
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'limit')}
                hasError={formErrors.limit}
                errorText={'Valid limit value is required'}
                tooltipText="Maximum times campaign shows within the time window. Example: Limit=3, Minutes=1440 means 'show max 3 times per 24 hours'. After reaching limit, cooldown period starts."
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Cooldown"
                inputType="number"
                placeholderText="Enter a positive cooldown value"
                value={formData.cooldown !== null ? formData.cooldown : ''}
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'cooldown')}
                hasError={formErrors.cooldown}
                errorText={'Valid cooldown value is required'}
                tooltipText="Hours to wait after hitting the limit before showing again. Example: Cooldown=24 means 'wait 24 hours after user sees campaign 3 times'. Use Case: Limit=1, Cooldown=168 = 'show once per week'."
              />
            </div>
          </div>
        </div>
        <CampaignManagerImageUpload uploadedImages={uploadedImages} setUploadedImages={handleSetUploadedImages} />
        <CampaignDisplayTypeFields
          displayType={formData.displayType}
          formData={formData}
          setFormData={setFormData}
          setFormErrors={setFormErrors}
          formErrors={formErrors}
          setHTMLPopUpTemplateFormData={setHTMLPopUpTemplateFormData}
          htmlPopUpTemplateFormData={htmlPopUpTemplateFormData}
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="w-[100px] h-[50px] px-4 py-2 mt-4 transition-colors rounded-md text-md bg-light-grey text-dark-grey font-semibold hover:bg-dark-purple hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="clear-button"
          onClick={() => handleClearForm()}
        >
          Clear
        </button>
        <Button
          type="submit"
          className="w-[200px] h-[50px] px-4 py-2 mt-4 font-bold text-white transition-colors rounded-md text-md bg-muted-dark-purple hover:bg-dark-purple disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-button"
          disabled={isLoading}
          endIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
          onClick={e => {
            // Blur the button after click to prevent focus retention during modal close
            (e.currentTarget as HTMLButtonElement).blur();
          }}
        >
          {isLoading ? 'Creating Campaign...' : 'Create Campaign'}
        </Button>
      </div>
    </form>
  );
};

export default CreateCampaignForm;
